import { CSSProperties, createContext } from 'react';

/**
 * Shared status chip look: a soft tinted background with the text in the status color
 * (darkened when the color is too light to read). Used by the status filters and status
 * badges on the Jobs (list, board, map), Leads, Invoices and Estimates pages.
 */
/** The "Paid" payment badge green (success-100 / success-800). */
const PAID_GREEN = { text: '#065f46', bg: '#d1fae5', solid: '#10b981' };

/** True for colors a person would call green (hue roughly 75°–165°, not grey). */
export function isGreenColor(hex?: string | null) {
  const clean = (hex || '').replace('#', '').trim();
  const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean.slice(0, 6);
  if (full.length !== 6) return false;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16) / 255);
  if ([r, g, b].some(Number.isNaN)) return false;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  if (max === 0 || delta / max < 0.25 || max !== g) return false;
  const hue = 60 * (((b - r) / delta) + 2);
  return hue >= 75 && hue <= 165;
}

/**
 * Tells the status chips which statuses are the company's "completed" status, so a green
 * Completed status shows in the same green as the Paid badge. Default: nothing is completed.
 */
export const CompletedStatusContext = createContext<(statusId?: string | null, statusName?: string | null) => boolean>(() => false);

/** Completed + green → the Paid green; otherwise the tinted look of the status color. */
export function chipColors(hex?: string | null, completed = false) {
  if (completed && isGreenColor(hex)) return PAID_GREEN;
  const clean = (hex || '').replace('#', '').trim();
  const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean.slice(0, 6);
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  if (full.length !== 6 || [r, g, b].some(Number.isNaN)) {
    return { text: '#475569', bg: 'rgba(100, 116, 139, 0.14)', solid: '#64748b' };
  }
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  const factor = brightness > 200 ? 0.45 : brightness > 150 ? 0.65 : 1;
  return {
    text: `rgb(${Math.round(r * factor)}, ${Math.round(g * factor)}, ${Math.round(b * factor)})`,
    bg: `rgba(${r}, ${g}, ${b}, 0.14)`,
    solid: `rgb(${r}, ${g}, ${b})`,
  };
}

/** Filter chip (the row of statuses above a list). */
export function statusFilterChipClass(selected: boolean) {
  return `inline-flex h-8 items-center gap-1.5 px-3 rounded-xl text-xs transition-all focus:outline-none select-none flex-shrink-0 border ${selected ? 'font-bold shadow-sm' : 'font-semibold border-transparent hover:brightness-95'}`;
}

/** Filter chip colors from a hex color. */
export function statusFilterChipStyle(hex: string | null | undefined, selected: boolean, completed = false): CSSProperties {
  const c = chipColors(hex, completed);
  return {
    backgroundColor: c.bg,
    color: c.text,
    borderColor: selected ? c.solid : 'transparent',
    boxShadow: selected ? `0 0 0 1px ${c.solid}` : undefined,
  };
}

/**
 * Filter chip for pages whose colors are Tailwind classes (e.g. "bg-green-100 text-green-700"):
 * the light background and dark text already match the tinted look.
 */
export function statusFilterChipTwClass(colorClasses: string, selected: boolean) {
  return `inline-flex h-8 items-center gap-1.5 px-3 rounded-xl text-xs transition-all focus:outline-none select-none flex-shrink-0 border ${colorClasses} ${selected ? 'font-bold border-current shadow-sm ring-1 ring-current' : 'font-semibold border-transparent hover:brightness-95'}`;
}

/** Small status badge inside a row or card. */
export const STATUS_BADGE_CLASS = 'inline-flex items-center gap-1.5 h-6 px-2.5 rounded-lg text-xs font-semibold w-fit';

export function statusBadgeStyle(hex?: string | null, completed = false): CSSProperties {
  const c = chipColors(hex, completed);
  return { backgroundColor: c.bg, color: c.text };
}

/** Hue / saturation of a hex color (null when it can't be read). */
function hexToHueSat(hex?: string | null) {
  const clean = (hex || '').replace('#', '').trim();
  const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean.slice(0, 6);
  if (full.length !== 6) return null;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16) / 255);
  if ([r, g, b].some(Number.isNaN)) return null;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  const l = (max + min) / 2;
  const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
  let h = 0;
  if (delta !== 0) {
    if (max === r) h = 60 * (((g - b) / delta) % 6);
    else if (max === g) h = 60 * ((b - r) / delta + 2);
    else h = 60 * ((r - g) / delta + 4);
  }
  return { h: (h + 360) % 360, s, l };
}

/**
 * Calendar job card: a pastel fill of the status color (same hue, instead of a see-through tint
 * that turns muddy), an outline a few shades darker and the solid status color as the stripe on
 * the left. Dark status colors get a deeper, calmer fill, so a dark green stays apart from a
 * bright green.
 */
export function calendarCardStyle(hex?: string | null, completed = false): CSSProperties {
  const c = chipColors(hex, completed);
  const hs = hexToHueSat(completed && isGreenColor(hex) ? PAID_GREEN.solid : hex);
  // Width / style / color per side (not `border` + `borderLeft`): when the outline color changes,
  // React re-applies `border` and it would wipe out the left stripe.
  const sides = (outline: string): CSSProperties => ({
    borderStyle: 'solid',
    borderWidth: '1px 1px 1px 4px',
    borderColor: `${outline} ${outline} ${outline} ${c.solid}`,
  });
  if (!hs || hs.s < 0.25) {
    return { backgroundColor: '#f1f5f9', color: '#1e293b', ...sides('#cbd5e1') };
  }
  const h = Math.round(hs.h);
  // 0 for a very dark color, 1 for a mid or light one
  const brightness = Math.min(hs.l / 0.5, 1);
  // 0 up to a mid color, 1 for a light, vivid one (e.g. a bright teal): those get a livelier fill
  const vivid = Math.min(Math.max((hs.l - 0.4) / 0.15, 0), 1);
  const baseSat = Math.max(hs.s, 0.6) * (0.55 + 0.45 * brightness);
  const s = Math.round((baseSat + (1 - baseSat) * vivid) * 100);
  const l = Math.round(82 + 12 * brightness - 5 * vivid);
  return {
    backgroundColor: `hsl(${h}, ${s}%, ${l}%)`,
    color: '#1e293b',
    ...sides(`hsl(${h}, ${s}%, ${l - 14}%)`),
  };
}

/** Color for the small dot at the start of a status badge. */
export function statusDotColor(hex?: string | null, completed = false) {
  return chipColors(hex, completed).solid;
}

/**
 * Gives each job the color its status has in the company's status settings (the same color
 * the status filters use), falling back to the color the job came with.
 */
export function withCompanyStatusColors<T extends { status?: unknown; status_id?: string; status_color?: string }>(
  jobs: T[],
  statuses: { id: string; name: string; color?: string; is_default_for_completed_job?: boolean }[],
): T[] {
  if (!statuses.length) return jobs;
  return jobs.map((job) => {
    const name = typeof job.status === 'string' ? job.status : (job.status as { name?: string } | undefined)?.name;
    const match = statuses.find((s) => job.status_id && s.id === job.status_id) || statuses.find((s) => s.name === name);
    // A green Completed status uses the same green as the Paid badge, like the status filters
    const color = match?.is_default_for_completed_job && isGreenColor(match.color) ? PAID_GREEN.solid : match?.color;
    return color && color !== job.status_color ? { ...job, status_color: color } : job;
  });
}
